// src/pages/Discipline.jsx
import React from 'react';
import { Shield, AlertTriangle, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { getMistakeData } from '../utils/stats';

export default function Discipline({ trades , lang = 'en'}) {
  const total = trades.length;
  const followed = trades.filter(t => t.followedRules === true || t.followedRules === 'true').length;
  const broken = total - followed;
  const pct = total ? Math.round((followed / total) * 100) : 0;
  const mistakes = getMistakeData(trades);

  const EMOTION_GROUPS = {};
  for (const t of trades) {
    const em = t.emotionBefore || 'Unknown';
    if (!EMOTION_GROUPS[em]) EMOTION_GROUPS[em] = { trades: 0, wins: 0, pnl: 0 };
    EMOTION_GROUPS[em].trades++;
    if (t.result === 'Win') EMOTION_GROUPS[em].wins++;
    EMOTION_GROUPS[em].pnl += Number(t.pnl || 0);
  }
  const emotionData = Object.entries(EMOTION_GROUPS)
    .sort((a, b) => b[1].trades - a[1].trades)
    .map(([emotion, d]) => ({
      emotion,
      trades: d.trades,
      winRate: Math.round((d.wins / d.trades) * 100),
      pnl: d.pnl
    }));

  const PAIR_GROUPS = {};
  for (const t of trades) {
    const p = t.pair || 'Unknown';
    if (!PAIR_GROUPS[p]) PAIR_GROUPS[p] = { trades: 0, wins: 0, pnl: 0 };
    PAIR_GROUPS[p].trades++;
    if (t.result === 'Win') PAIR_GROUPS[p].wins++;
    PAIR_GROUPS[p].pnl += Number(t.pnl || 0);
  }
  const pairData = Object.entries(PAIR_GROUPS)
    .sort((a, b) => b[1].trades - a[1].trades)
    .map(([pair, d]) => ({
      pair,
      trades: d.trades,
      winRate: Math.round((d.wins / d.trades) * 100),
      pnl: d.pnl
    }));

  const disciplineLabel = pct >= 90 ? 'Elite' : pct >= 75 ? 'Strong' : pct >= 60 ? 'Fair' : 'Needs Work';
  const disciplineColor = pct >= 90 ? '#00e5a0' : pct >= 75 ? '#00c98a' : pct >= 60 ? '#ffd166' : '#ff4d6d';

  return (
    <div className="fade-in" style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#f0f0f5', margin: 0 }}>Discipline Tracker</h1>
        <p style={{ color: '#8888a8', fontSize: 14, margin: '4px 0 0' }}>Track rule-following, mistakes, and emotional patterns.</p>
      </div>

      {/* Score Banner */}
      <div style={{
        background: '#16161f', border: `1px solid ${disciplineColor}30`,
        borderRadius: 14, padding: '28px 32px', marginBottom: 24,
        display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', width: 90, height: 90 }}>
          <svg width="90" height="90" viewBox="0 0 90 90">
            <circle cx="45" cy="45" r="38" fill="none" stroke="#1e1e2e" strokeWidth="8" />
            <circle cx="45" cy="45" r="38" fill="none" stroke={disciplineColor} strokeWidth="8"
              strokeDasharray={`${2 * Math.PI * 38 * pct / 100} ${2 * Math.PI * 38 * (1 - pct / 100)}`}
              strokeDashoffset={2 * Math.PI * 38 * 0.25}
              strokeLinecap="round"
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{ fontSize: 20, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: disciplineColor }}>
              {pct}%
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Discipline Score
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, color: disciplineColor, fontFamily: "'DM Mono', monospace", marginTop: 4 }}>
            {disciplineLabel}
          </div>
          <div style={{ fontSize: 13, color: '#8888a8', marginTop: 4 }}>
            {followed} of {total} trades followed your rules
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, justifyContent: 'center' }}>
              <CheckCircle size={14} style={{ color: '#00e5a0' }} />
              <span style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em' }}>Rules Followed</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#00e5a0', fontFamily: "'DM Mono', monospace" }}>{followed}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, justifyContent: 'center' }}>
              <XCircle size={14} style={{ color: '#ff4d6d' }} />
              <span style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em' }}>Rules Broken</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>{broken}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Mistakes Breakdown */}
        <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <AlertTriangle size={15} style={{ color: '#ffd166' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Mistake Breakdown</span>
          </div>
          {mistakes.length === 0 ? (
            <div style={{ color: '#55556a', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No mistakes recorded</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mistakes.map((m, i) => {
                const maxVal = mistakes[0].value;
                const pct = Math.round((m.value / maxVal) * 100);
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: '#f0f0f5' }}>{m.name}</span>
                      <span style={{ fontSize: 12, color: '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>{m.value}x</span>
                    </div>
                    <div style={{ background: '#1e1e2e', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#ff4d6d', borderRadius: 4, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Emotion Performance */}
        <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <TrendingUp size={15} style={{ color: '#4cc9f0' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Performance by Emotion</span>
          </div>
          {emotionData.length === 0 ? (
            <div style={{ color: '#55556a', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No emotion data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {emotionData.map((e, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: 8, border: '1px solid #1e1e2e'
                }}>
                  <span style={{ fontSize: 13, color: '#f0f0f5', flex: 1, minWidth: 80 }}>{e.emotion}</span>
                  <span style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace" }}>{e.trades}tr</span>
                  <span style={{
                    fontSize: 12, fontFamily: "'DM Mono', monospace", fontWeight: 600,
                    color: e.winRate >= 60 ? '#00e5a0' : e.winRate >= 40 ? '#ffd166' : '#ff4d6d'
                  }}>{e.winRate}% WR</span>
                  <span style={{
                    fontSize: 12, fontFamily: "'DM Mono', monospace",
                    color: e.pnl >= 0 ? '#00e5a0' : '#ff4d6d'
                  }}>{e.pnl >= 0 ? '+' : ''}${e.pnl.toFixed(0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pair Performance */}
      <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <Shield size={15} style={{ color: '#00e5a0' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Performance by Pair</span>
        </div>
        {pairData.length === 0 ? (
          <div style={{ color: '#55556a', fontSize: 13 }}>No data yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {pairData.map((p, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid #1e1e2e',
                borderRadius: 8, padding: '14px 16px'
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5', marginBottom: 8 }}>{p.pair}</div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                  <span style={{ color: '#55556a' }}>{p.trades} trades</span>
                  <span style={{ color: p.winRate >= 60 ? '#00e5a0' : p.winRate >= 40 ? '#ffd166' : '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>
                    {p.winRate}% WR
                  </span>
                  <span style={{ color: p.pnl >= 0 ? '#00e5a0' : '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>
                    {p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
